import {AxiosProgressEvent, CancelToken} from 'axios';
import parseFileName from '../../utils/fileUtils/parseFileName';
import wait from '../../utils/promises/wait';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {FeedAdvancedParams, GoogleSheetDetails} from '../feeds/feeds.types';
import {fileUploadApi} from '../fileUpload/fileUpload.api';
import {JobExecutionParams} from '../jobs/jobs.types';
import waitForJob from '../jobs/utils/waitForJob';
import {TaxonomyEntry} from '../taxonomy/taxonomy.types';
import {
  toClientLocationFeed,
  toRemoteMapping,
  toRemoteMappingRule,
} from './locationFeed.dataConverter';
import {FeedFileMetadataState, FeedUploadMethod} from './locationFeed.enums';
import {
  FeedMappingDictionary,
  FeedMappingRule,
  LocationFeed,
  RemoteLocationFeed,
} from './locationFeed.types';
import getFeedRuleKey from './utils/getFeedRuleKey';
import getFeedRuleValue from './utils/getFeedRuleValue';

const basePath = `LocationFeeds`;

const FEED_META_UPDATE_INTERVAL = 2000;
const FEED_META_UPDATE_MAX_COUNT = 50;

// This call returns an array with an inner array of feeds
async function getFeeds(cancelToken?: CancelToken): Promise<LocationFeed[]> {
  const {data} = await apiService.executeGetRequest<Array<RemoteLocationFeed[]>>(
    `${basePath}/GetAll`,
    {},
    {cancelToken},
  );
  const [feedItemsResponse] = data;

  return feedItemsResponse.map(toClientLocationFeed);
}

async function getFeedsNew(cancelToken?: CancelToken): Promise<LocationFeed[]> {
  const {data} = await apiV2Service.executeGetRequest<Array<RemoteLocationFeed>>(
    `${basePath}/GetAll`,
    {},
    {cancelToken},
  );

  return data.map(toClientLocationFeed);
}

function getDownloadUrl(locationFeedId: number): Promise<string> {
  return apiService
    .executeGetRequest<[string]>(`${basePath}/GetCurrentLocationFeedFile`, {
      locationFeedId,
    })
    .then(({data: [fileUrl]}) => fileUrl);
}

async function uploadInventoryFile({
  locationFeedId,
  file,
  onUploadProgress,
  cancelToken,
}: {
  locationFeedId: number;
  file: File;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  cancelToken?: CancelToken;
}) {
  const urlObj = await fileUploadApi.getPreSignedUrlForLocationFeedUpload({
    locationFeedId,
    contentType: file.type,
    fileExtension: parseFileName(file.name).fileExtension ?? '',
  });

  await fileUploadApi.uploadFileToS3({
    url: urlObj.presignedUrl,
    file,
    onUploadProgress,
    cancelToken,
    contentType: file.type,
  });

  return urlObj;
}

function updateFileSourceConfig({
  customParams,
  fileUrl,
  cronExpression,
  id,
  timeZone,
  feedUploadMethod,
  googleSheetDetails,
}: {
  feedUploadMethod: FeedUploadMethod;
  fileUrl: string;
  cronExpression: string;
  id: number;
  timeZone: string;
  customParams?: Record<string, string>;
  googleSheetDetails?: GoogleSheetDetails;
}) {
  const formData = {
    Data: JSON.stringify({
      customParams,
      googleSheetDetails,
    }),
  };

  return apiService.executePostRequest<void>(
    `${basePath}/UpdateFileSourceConfig`,
    {
      feedUploadMethod,
      fileUrl,
      id,
      ...(timeZone && {timeZone}),
      ...(cronExpression && {cronExpression}),
    },
    formData,
  );
}

function get(id: number): Promise<LocationFeed> {
  return apiService
    .executeGetRequest<RemoteLocationFeed[]>(`${basePath}/Get`, {
      id,
    })
    .then(({data: [locationFeed]}) => toClientLocationFeed(locationFeed));
}

function create(params: {name: string; feedType: number}): Promise<number> {
  return apiService
    .executePostRequest<[number]>(`${basePath}/Add`, {
      ...params,
      fileUrl: '',
      cronExpression: '',
      fileUploadMethod: 0,
    })
    .then(({data: [newFeedId]}) => newFeedId);
}

function remove(id: number): Promise<unknown> {
  return apiService.executeDeleteRequest<unknown>(`${basePath}/Delete`, {id});
}

function update(
  feedId: number,
  data: {name?: string; feedType?: number; feedUploadMethod?: FeedUploadMethod},
) {
  return apiService.executePostRequest<unknown>(`${basePath}/Update`, {
    id: feedId,
    ...data,
  });
}

function getMappingDictionary(): Promise<TaxonomyEntry[]> {
  return apiService
    .executeGetRequest<[FeedMappingDictionary]>(`${basePath}/GetFullLocationFeedMappingDic`)
    .then(({data: [dictionary]}) =>
      Object.values(dictionary).map(({shortName, fullName, isRequired}) => ({
        value: shortName,
        label: fullName,
        description: '',
        isRequired,
        defaultUIName: fullName,
      })),
    );
}

function updateFeedMapping(locationFeedId: number, data: LocationFeed['mapping']) {
  const payload = toRemoteMapping(data);

  return apiService.executePostRequest<unknown>(
    `${basePath}/UpdateLocationFeedMapping`,
    {locationFeedId},
    {Data: JSON.stringify(payload)},
  );
}

function updateFeedMappingAdvancedParams(
  locationFeedId: number,
  data: Partial<FeedAdvancedParams>,
) {
  return apiService.executePostRequest<unknown>(
    `${basePath}/UpdateFeedMappingAdvancedParameters`,
    {locationFeedId},
    {Data: JSON.stringify(data)},
  );
}

function updateFeedMappingRules(feedId: number, rules: FeedMappingRule[]) {
  const payload = rules
    .filter((rule) => !!rule.columnName)
    .map((rule) => ({
      ...rule,
      capitalization: 0,
      defaultValue: '',
      overrideValue: '',
      regexExtract: '',
      regexReplace: [],
      replace: [],
      [getFeedRuleKey(rule.ruleType)]: getFeedRuleValue(rule.ruleType, rule),
    }))
    .map(toRemoteMappingRule);

  return apiService.executePostRequest<unknown>(
    `${basePath}/UpdateFeedMappingRules`,
    {feedId, isEnrich: false},
    {Data: JSON.stringify(payload)},
  );
}

async function updateInventory(locationFeedId: number) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`${basePath}/${locationFeedId}/updateInventory`)
    .then(({data: {executionId: jobId}}) => {
      waitForJob({
        jobId,
        jobName: `Update Feed LF${locationFeedId} Inventory Items`,
      });
    });
}

async function updateFeedMetaData(feedId: number) {
  await apiService.executePostRequest<unknown>(`${basePath}/StartFeedFileMetadataUpdate`, {
    feedId,
  });

  let idx = 0;

  await wait(FEED_META_UPDATE_INTERVAL);

  while (idx < FEED_META_UPDATE_MAX_COUNT) {
    const {data} = await apiService.executeGetRequest<
      [
        {
          feedFileMetadataState: FeedFileMetadataState;
          exception: string;
          columnNameToValuesExamplesMap: Record<string, string[]>;
        },
      ]
    >(`${basePath}/GetFeedFileMetadata`, {
      feedId,
    });
    const {feedFileMetadataState, exception, columnNameToValuesExamplesMap} = data[0];

    switch (feedFileMetadataState) {
      case FeedFileMetadataState.FailedToUpdate:
        throw new Error(exception);
      case FeedFileMetadataState.NeverRun:
        return null;
      case FeedFileMetadataState.ReadyForUse:
        return columnNameToValuesExamplesMap;
      case FeedFileMetadataState.Updating:
        await wait(FEED_META_UPDATE_INTERVAL);
        break;
    }

    idx++;
  }

  throw new Error('Updating took too long. Mapping will appear after update finished');
}

function getFeedFileWarnings(feedId: number) {
  return apiService
    .executeGetRequest<[{exception: string}]>(`${basePath}/GetFeedFileMetadata`, {
      feedId,
    })
    .then(({data}) => data[0].exception);
}

async function duplicateLocationFeed(feedId: number) {
  const {data} = await apiV2Service.executePostRequest<number>(`${basePath}/${feedId}/duplicate`);

  return data;
}

async function searchFeeds(prompt: string) {
  const {data} = await apiV2Service.executeGetRequest<{
    locationFeeds: RemoteLocationFeed[];
    filtersExplanation: string;
  }>(`${basePath}/SearchLocationFeeds`, {
    prompt,
  });

  return data;
}

const locationFeedApi = {
  get,
  getFeedsNew,
  getFeeds,
  delete: remove,
  uploadInventoryFile,
  update,
  getDownloadUrl,
  getFeedFileWarnings,
  updateFileSourceConfig,
  create,
  updateFeedMetaData,
  getMappingDictionary,
  updateFeedMapping,
  updateFeedMappingAdvancedParams,
  updateFeedMappingRules,
  updateInventory,
  duplicateLocationFeed,
  searchFeeds,
};

export default locationFeedApi;
